/**
 * Single source of truth for Auth0 / auth configuration.
 *
 * Rules:
 *   1. If `REACT_APP_AUTH0_CLIENT_ID` is unset we treat auth as fully
 *      disabled (local dev without Auth0). Consumers see `authEnabled: false`
 *      and the app runs without any redirects or token handling.
 *   2. If `REACT_APP_AUTH0_CLIENT_ID` is set, the other Auth0 env vars are
 *      mandatory. Missing ones are surfaced via `missingAuthEnvVars()` and
 *      the app shows an explicit config-error page rather than falling back
 *      to defaults (which masked real misconfigurations in prior versions).
 */

const raw = {
  clientId: (process.env.REACT_APP_AUTH0_CLIENT_ID || '').trim(),
  domain: (process.env.REACT_APP_AUTH0_DOMAIN || '').trim(),
  audience: (process.env.REACT_APP_AUTH0_AUDIENCE || '').trim(),
} as const;

export const authEnabled = !!raw.clientId;

export const auth0Config = {
  clientId: raw.clientId,
  domain: raw.domain,
  audience: raw.audience,
};

/** Names of the env vars that are required when auth is enabled but are unset. */
export const missingAuthEnvVars = (): string[] => {
  if (!authEnabled) return [];
  const missing: string[] = [];
  if (!raw.domain) missing.push('REACT_APP_AUTH0_DOMAIN');
  if (!raw.audience) missing.push('REACT_APP_AUTH0_AUDIENCE');
  return missing;
};

/** True when auth is enabled and fully configured. */
export const authConfigValid = (): boolean => authEnabled && missingAuthEnvVars().length === 0;
